import React, { useEffect, useState } from "react";
import "./CSS/AIAnalysis.css";
import AIReasoningModal from "./AIReasoningModal";
import supabase from "../../../supabaseClient";
import { lightHaptic } from "../../../native/capacitor";

const mapChildResponse = (val) => {
  if (val === undefined || val === null) return null;
  const trimmed = String(val).trim();
  if (["-", "—", "--"].includes(trimmed)) return null;
  const clean = trimmed.toLowerCase();
  const map = {
    "vocal": "Vocal response",
    "vocal response": "Vocal response",
    "gesture": "Gesture response",
    "gesture response": "Gesture response",
    "multi word response": "Multi word response",
    "single word response": "Single word response",
    "multi word initiation": "Multi word initiation",
    "single word initiation": "Single word initiation",
    "vocal initiation": "Vocal initiation",
    "gesture initiation": "Gesture initiation",
    "none": null,
  };
  return map[clean] || val;
};

const formatChildTranscript = (response, transcript) => {
  const mapped = mapChildResponse(response);
  if (!mapped) return "-";
  const isGesture = /gesture/i.test(mapped);
  if (isGesture || !transcript || typeof transcript !== "string") return "-";
  const trimmedTranscript = transcript.trim();
  if (["-", "—", "--"].includes(trimmedTranscript)) return "-";
  const parts = trimmedTranscript.split("||").map((p) => p.trim()).filter(Boolean);
  const kids = parts
    .map((p) => {
      const m = p.match(/^(kid|child)\s*[:-]\s*(.*)$/i);
      return m && m[2] ? m[2].trim() : null;
    })
    .filter(Boolean);
  if (kids.length) return kids.join(", ");
  if (isGesture && (!trimmedTranscript || trimmedTranscript === "-")) return "-";
  return trimmedTranscript || "-";
};

const AIAnalysis = ({ profile, analysisResults, videoId, seekToTime, isMobile, analysisReady, isDemo, isGuest }) => {
  const [overallScore, setOverallScore] = useState(0);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [showScores, setShowScores] = useState(true);
  const [showStrategyModal, setShowStrategyModal] = useState(false);
  const [strategyKey, setStrategyKey] = useState(null);

  const STRATEGY_VIDEOS = {
    "Modeling": "/assets/strategy-videos/Modeling.mp4",
    "Mand-model": "/assets/strategy-videos/MandModel.mp4",
    "Time Delay": "/assets/strategy-videos/TimeDelay.mp4",
  };

  const openStrategyModal = (strategy) => {
    lightHaptic();
    setStrategyKey(strategy);
    setShowStrategyModal(true);
  };
  const closeStrategyModal = () => setShowStrategyModal(false);


  const handleToggleScore = async () => {
    const newValue = !showScores;
    setShowScores(newValue);

    const { error } = await supabase
      .from("profiles")
      .update({ show_score: newValue })
      .eq("id", profile.id);

    if (error) {
      console.error("Failed to update show_score:", error.message);
      setShowScores(!newValue);
    }
  };

  useEffect(() => {
    if (profile?.show_score !== undefined) {
      setShowScores(profile.show_score);
    }
  }, [profile]);

  const visibleResults = React.useMemo(
    () => analysisResults.filter((r) => r.is_deleted !== true),
    [analysisResults]
  );

  useEffect(() => {
    if (!analysisResults.length) return;
    console.log("[UserAnalysis] sample rows", analysisResults.slice(0, 3));
  }, [analysisResults]);

  useEffect(() => {
    if (visibleResults.length === 0) return;

    const totalScore = visibleResults.reduce(
      (sum, result) => sum + (parseInt(result["Fidelity Score"], 10) / 4) * 100,
      0
    );

    setOverallScore((totalScore / visibleResults.length).toFixed(0));
  }, [visibleResults]);

  const handleSeek = (timestamp) => {
    const [minutes, seconds] = timestamp.split(":").map(Number);
    const timeInSeconds = minutes * 60 + seconds;
    seekToTime(timeInSeconds);
  };

  const truncateText = (text, limit = 100) => {
    if (!text) return "";
    if (text.length <= limit) return text;
    return text.slice(0, limit) + "...";
  };

  const handleReadMore = (rowObj) => {
    lightHaptic();
    setDetailData(rowObj);
    setShowDetailModal(true);
  };

  return (
    <div className="ai-analysis-section">
      {analysisReady && (
        <div className="ai-header">
          {showScores && (
            <span className="overall-score">
              Overall Score: {overallScore}
            </span>
          )}
          <label className="score-switch">
            <input
              type="checkbox"
              checked={showScores}
              onChange={handleToggleScore}
            />
            <span className="slider" />
            <span className="switch-label">
              {showScores ? "Scores" : "Scores"}
            </span>
          </label>
        </div>
      )}
      <div className={`ai-table-container ${isMobile ? "mobile" : ""}`}>
        {!analysisReady || visibleResults.length === 0 ? (
          <div className="analysis-banner">
            {!analysisReady ? (
              isDemo
                ? "Waiting for analysis..."
                : isGuest
                  ? "Your video is being analyzed. This usually takes a couple of minutes…"
                  : "Your video is being analyzed. We’ll email you as soon as it’s ready."
            ) : (
              "Your analysis is ready! If you don’t see the table, refresh the page."
            )}
          </div>
        ) : (
          <table className="ai-analysis-table">
            <thead>
              <tr>
                <th>Time</th>
                {showScores && <th>Score</th>}
                <th>Summary</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visibleResults.map((result, index) => {
                const beginEnd = result["Begin-End"];
                const time = typeof beginEnd === "string" && beginEnd.includes("-")
                  ? beginEnd.split("-")[0]
                  : "00:00";
                const fullReasoning = result["Feedback"] || "";
                const childResponseRaw =
                  result["Child Response"] ??
                  result.child_response ??
                  result.childResponse ??
                  result.child ??
                  "";
                const childTranscriptRaw =
                  result["Child Transcript"] ??
                  result.all_sentences ??
                  result.child_transcript ??
                  "";
                const childResponseDisplay = mapChildResponse(childResponseRaw);
                const childTranscriptDisplay = formatChildTranscript(
                  childResponseRaw,
                  childTranscriptRaw
                );
                const showChildModal =
                  childResponseDisplay &&
                  childResponseDisplay !== "-" &&
                  childResponseDisplay !== "—"
                    ? childResponseDisplay
                    : null;
                const showTranscriptModal =
                  childTranscriptDisplay &&
                  childTranscriptDisplay !== "-" &&
                  childTranscriptDisplay !== "—"
                    ? childTranscriptDisplay
                    : null;
                const summaryLimit = isMobile ? 100 : 160;
                const truncated = truncateText(fullReasoning, summaryLimit);

                return (
                  <tr key={index} className="fade-in">
                    <td
                      className="timestamp-button-analysis"
                      onClick={() => handleSeek(time)}
                    >
                      {time}
                    </td>
                    {showScores && (
                      <td style={{ fontWeight: 700, textAlign: "center" }}>
                        {result["Fidelity Score"]}
                      </td>
                    )}
                    <td>
                      <>
                        {truncated}
                        <button
                          className="read-more-btn"
                          onClick={() =>
                            handleReadMore({
                              time,
                              score: result["Fidelity Score"],
                              reasoning: fullReasoning,
                              childResponse: showChildModal,
                              childTranscript: showTranscriptModal,
                            })
                          }
                        >
                          Read More
                        </button>
                      </>
                    </td>
                    <td className="info-cell">
                      <button
                        className="info-btn"
                        onClick={() => openStrategyModal(result["Strategy"])}
                        aria-label="Strategy details"
                      >
                        ℹ︎
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showDetailModal && (
        <AIReasoningModal
          showScores={showScores}
          rowData={detailData}
          onClose={() => setShowDetailModal(false)}
        />
      )}
      {showStrategyModal && (
        <div className="modal-overlay" onClick={closeStrategyModal}>
          <div className="strategy-modal" onClick={(e) => e.stopPropagation()}>
            <div className="strategy-modal-body">
              <h3>{strategyKey} Strategy</h3>

              {strategyKey === "Modeling" && (
                <>
                  {STRATEGY_VIDEOS[strategyKey] && (
                    <div className="video-wrapper">
                      <video
                        src={STRATEGY_VIDEOS[strategyKey]}
                        controls
                        playsInline
                        className="strategy-video"
                      />
                    </div>
                  )}
                  <p>These strategies help your child learn and use new words.</p>
                  <p><strong>Modeling</strong> means showing the word:</p>
                  <ul>
                    <li>You point to a picture and say, “car.”</li>
                  </ul>
                  <p className="mt-2 font-medium">Steps</p>
                  <ol>
                    <li>Join your child’s attention – look at what your child is focused on in the book.</li>
                    <li>Model – say the word or ask a simple question.</li>
                    <li>Pause and wait – wait 3–5 s to give your child time.</li>
                    <li>Respond – smile, praise, and repeat their correct response or gently try again.</li>
                  </ol>
                  <p className="mt-2">These steps make learning feel natural and fun.</p>
                </>
              )}

              {strategyKey === "Mand-model" && (
                <>
                  {STRATEGY_VIDEOS[strategyKey] && (
                    <div className="video-wrapper">
                      <video
                        src={STRATEGY_VIDEOS[strategyKey]}
                        controls
                        playsInline
                        className="strategy-video"
                      />
                    </div>
                  )}
                  <p><strong>Mand-Model</strong> means encouraging your child to respond:</p>
                  <ul>
                    <li>You say, “Where is the car?” or “Say ‘car.’”</li>
                  </ul>
                  <p className="mt-2 font-medium">Steps</p>
                  <ol>
                    <li>Join your child’s attention – look at what your child is focused on in the book.</li>
                    <li>Model or ask – say the word or ask a simple question.</li>
                    <li>Pause and wait – wait 3–5 s.</li>
                    <li>Respond – smile, praise, and repeat their correct response or gently try again.</li>
                  </ol>
                  <p className="mt-2">These steps make learning feel natural and fun.</p>
                </>
              )}

              {strategyKey === "Time Delay" && (
                <>
                  {STRATEGY_VIDEOS[strategyKey] && (
                    <div className="video-wrapper">
                      <video
                        src={STRATEGY_VIDEOS[strategyKey]}
                        controls
                        playsInline
                        className="strategy-video"
                      />
                    </div>
                  )}
                  <p>This strategy encourages your child to speak or gesture by giving them time to fill in a missing word or phrase.</p>
                  <p><strong>Example</strong>: You point to a ball and say, “This is a _____,” and wait.</p>
                  <p className="mt-2 font-medium">Steps</p>
                  <ol>
                    <li>Join your child’s attention – focus on what they are looking at in the book.</li>
                    <li>Leave a sentence incomplete – start reading, then stop before the last word.</li>
                    <li>Wait 3–5 s – look expectantly at your child.</li>
                    <li>Respond – repeat their word, praise their effort, or say the phrase again if they don’t respond.</li>
                  </ol>
                  <p className="mt-2">Extra tip: re-read favourite pages and rhymes often. Familiar stories help your child remember and participate!</p>
                </>
              )}
            </div>

            <div className="strategy-modal-footer">
              <button className="modal-close" onClick={closeStrategyModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAnalysis;
